import React, { useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Download, Upload, Loader2, ShieldCheck } from 'lucide-react';

const LABELS = {
  courses: 'Courses', modules: 'Modules', elements: 'Lessons & quiz items', course_live_versions: 'Live course versions',
  categories: 'Categories', bundles: 'Bundles', bundle_categories: 'Bundle categories', questions: 'Question bank',
  hub_articles: 'Hub articles', hub_downloads: 'Hub downloads', hub_products: 'Hub e-books', custom_pages: 'Website pages',
  homepage_content: 'Homepage', cms_settings: 'Site settings', navigation_items: 'Menu items',
};
const errText = (e, f) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : f);

const Report = ({ result }) => {
  const key = result.dry_run ? 'to_add' : 'added';
  const total = Object.values(result.collections).reduce((s, c) => s + c[key], 0);
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="backup-restore-report">
      <p className="font-medium text-[#2a3e5d]" data-testid="backup-restore-summary">
        {result.dry_run ? (total ? `${total} missing item(s) will be added back.` : 'Nothing is missing — everything in this backup is already here.') : `Restore complete — ${total} item(s) added back.`}
      </p>
      {result.exported_at && <p className="text-xs text-slate-500 mt-0.5">Backup made {new Date(result.exported_at).toLocaleString('en-GB')}</p>}
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-3">
        {Object.entries(result.collections).filter(([, c]) => c.in_file).map(([k, c]) => (
          <div key={k} className="flex justify-between text-xs"><span className="text-slate-600">{LABELS[k] || k}</span>
            <span className={c[key] ? 'text-green-700 font-medium' : 'text-slate-400'}>{c[key] ? `+${c[key]}` : 'all present'} / {c.in_file}</span></div>
        ))}
      </div>
    </div>
  );
};

export const ContentBackupCard = () => {
  const { api } = useAuth();
  const [busy, setBusy] = useState(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const download = async () => {
    setBusy('export');
    try {
      const r = await api.get('/admin/backup/export', { responseType: 'blob' });
      const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'common-impact-backup.json';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Backup downloaded — keep it somewhere safe');
    } catch { toast.error('Could not create the backup'); } finally { setBusy(null); }
  };

  const restore = async (f, dryRun) => {
    setBusy(dryRun ? 'check' : 'restore');
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post(`/admin/backup/restore?dry_run=${dryRun}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      if (!dryRun) { toast.success('Restore complete'); setFile(null); }
    } catch (e) { toast.error(errText(e, 'Could not read this backup')); setResult(null); setFile(null); } finally { setBusy(null); }
  };

  const pick = (f) => { if (!f) return; setFile(f); setResult(null); restore(f, true); if (inputRef.current) inputRef.current.value = ''; };
  const toAdd = result?.dry_run ? Object.values(result.collections).reduce((s, c) => s + c.to_add, 0) : 0;

  return (
    <Card className="border-slate-200" data-testid="content-backup-card">
      <CardHeader><CardTitle className="text-lg flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#20acf6]" />Content backup</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">Download a copy of all courses (modules, lessons, quizzes), categories, bundles, question bank, Knowledge Hub and website pages. Uploaded images and files stay in storage and are linked, not copied.</p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={download} disabled={!!busy} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="backup-download-btn">
            {busy === 'export' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Download backup
          </Button>
          <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={!!busy} data-testid="backup-restore-choose-btn">
            {busy === 'check' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Restore from a backup…
          </Button>
          <input ref={inputRef} type="file" accept=".json,application/json" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="backup-restore-file" />
        </div>
        <p className="text-xs text-slate-500">Restoring only adds back items that are missing. It never changes or deletes anything that already exists.</p>
        {result && <Report result={result} />}
        {result?.dry_run && toAdd > 0 && file && (
          <Button onClick={() => restore(file, false)} disabled={!!busy} className="bg-green-600 hover:bg-green-700" data-testid="backup-restore-confirm-btn">
            {busy === 'restore' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add back {toAdd} missing item(s)
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
